import React from 'react';

import { forwardRef } from '@chakra-ui/react';

import { warnDeprecated } from '../../helpers/deprecation';
import { resolveVersion } from '../../helpers/resolve-version';
import { HeadingProps, HeadingV21Props, HeadingV22Props } from './types';
import { HEADING_VERSIONS } from './versions';
import { HeadingV21 } from './versions/2.1/heading';
import { HeadingV22 } from './versions/2.2/heading';

export const Heading = forwardRef<HeadingProps, 'h2'>(function Heading(
  { v, ...rest },
  ref
) {
  const version = resolveVersion(HEADING_VERSIONS, v);
  warnDeprecated(HEADING_VERSIONS, version, v);

  // Boundary casts: `v` is what discriminates `HeadingProps`, so destructuring it off leaves
  // `rest` holding the union of both versions' `size`. The branch above has already resolved
  // which version renders, which the type system cannot follow through `resolveVersion`.
  // Both branches stamp `data-dls-version`: the merged style-config keeps unstamped elements on
  // 2.1, because `Heading` imported straight from `@chakra-ui/react` never reaches this dispatcher
  // and has to keep rendering the scale it always did.
  if (version === '2.1') {
    return (
      <HeadingV21
        ref={ref}
        data-dls-version="2.1"
        {...(rest as HeadingV21Props)}
      />
    );
  }

  return (
    <HeadingV22
      ref={ref}
      data-dls-version="2.2"
      {...(rest as HeadingV22Props)}
    />
  );
});

Heading.displayName = 'Heading';
